import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://nissi-india.com"; // change if needed

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}
function s(v) {
  return (v ?? "").toString().trim();
}

const EmployeeOverallReportPrint = () => {
  const q = useQuery();

  const finYearId = s(q.get("finYearId"));
  const branchId = s(q.get("branchId"));
  const branchType = s(q.get("branchType"));
  const site = s(q.get("site")); // optional branch filter

  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState({});
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    totalEmployees: 0,
    totalResignation: 0,
    totalCurrent: 0,
  });

  const reportTitle = "EMPLOYEE OVERALL REPORT";

  const fetchAll = async () => {
    if (!finYearId || !branchId || !branchType) return;

    try {
      setLoading(true);

      const [metaRes, dataRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/employee-overall-view/print-meta`, {
          params: { finYearId: Number(finYearId) },
        }),
        axios.get(`${BASE_URL}/api/employee-overall-view/print-data`, {
          params: {
            finYearId: Number(finYearId),
            branchId: Number(branchId),
            branchType,
            site: site || "",
          },
        }),
      ]);

      if (metaRes?.data?.success) setCompany(metaRes.data.row || {});
      if (dataRes?.data?.success) {
        setRows(dataRes.data.rows || []);
        setTotals({
          totalEmployees: dataRes.data?.totals?.totalEmployees || 0,
          totalResignation: dataRes.data?.totals?.totalResignation || 0,
          totalCurrent: dataRes.data?.totals?.totalCurrent || 0,
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Company header mapping (safe defaults)
  const companyName =
    company?.fld_companyname ||
    company?.CompanyName ||
    company?.companyname ||
    "Nissi Infotech";

  const companyLine2 =
    company?.fld_companytype ||
    company?.CompanyType ||
    company?.companytype ||
    "CIVIL CONSTRUCTION";

  const address =
    company?.fld_companyaddress ||
    company?.CompanyAddress ||
    company?.companyaddress ||
    "3/42-12, Nagappa colony, Kavundampalayam, Coimbatore - 641030, Tamil Nadu.";

  const phone =
    company?.fld_phone ||
    company?.Phone ||
    company?.phone ||
    "";

  const tagline =
    company?.fld_email ||
    company?.Email ||
    company?.email ||
    "infraconsoft-implementations";

  const branchLabel = site ? site : "ALL";

  const onPrint = () => window.print();
  const onClose = () => window.close();

  return (
    <div style={{ padding: 18, background: "#fff" }}>
      <style>{`
        * { box-sizing: border-box; }
        body { background: #fff; }
        .page {
          border: 1px solid #000;
          padding: 14px;
        }
        .top-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-bottom: 10px;
        }
        .btn {
          padding: 8px 18px;
          border: 1px solid #000;
          background: #000;
          color: #fff;
          font-weight: 700;
          cursor: pointer;
        }
        .btn.secondary {
          background: #fff;
          color: #000;
        }
        .header {
          border: 1px solid #000;
          padding: 10px;
        }
        .header-grid {
          display: grid;
          grid-template-columns: 120px 1fr;
          gap: 10px;
          align-items: center;
        }
        .logo-box {
          width: 110px;
          height: 90px;
          border: 1px solid #000;
        }
        .company {
          text-align: center;
          line-height: 1.35;
        }
        .company .name { font-size: 24px; font-weight: 800; }
        .company .type { font-size: 14px; font-weight: 800; margin-top: 4px; }
        .company .addr { font-size: 12px; margin-top: 6px; }
        .divider {
          border-top: 1px solid #000;
          margin: 12px 0;
        }
        .title {
          text-align: center;
          font-weight: 900;
          letter-spacing: 0.5px;
          margin: 6px 0 12px;
          font-size: 14px;
        }
        .filters {
          font-size: 12px;
          margin-bottom: 10px;
        }
        table.report {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        table.report th, table.report td {
          border: 1px solid #000;
          padding: 6px 6px;
          text-align: center;
          vertical-align: middle;
          white-space: nowrap;
        }
        table.report th { font-weight: 900; }
        table.report td.left { text-align: left; }
        tfoot td {
          font-weight: 900;
          background: #f5f5f5;
        }
        .note {
          margin-top: 10px;
          font-size: 12px;
          font-weight: 700;
        }
        .muted { font-weight: 500; }

        @media print {
          .top-actions { display: none !important; }
          .page { border: none; padding: 0; }
          @page { margin: 10mm; }
        }
      `}</style>

      {/* Buttons */}
      <div className="top-actions">
        <button className="btn" onClick={onPrint}>Print</button>
        <button className="btn secondary" onClick={onClose}>Close</button>
      </div>

      <div className="page">
        {/* Header */}
        <div className="header">
          <div className="header-grid">
            <div className="logo-box" />
            <div className="company">
              <div className="name">{companyName}</div>
              <div className="type">{companyLine2}</div>
              <div className="addr">{address}</div>
              {phone ? <div className="addr">Phone No : {phone}</div> : null}
              <div className="addr">{tagline}</div>
            </div>
          </div>

          <div className="divider" />
          <div className="title">{reportTitle}</div>
        </div>

        {/* Filters */}
        <div className="filters">
          <div>
            <b>Branch :</b> <span className="muted">{branchLabel}</span>
          </div>
        </div>

        {/* Table */}
        <table className="report">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Department</th>
              <th>Total Employees</th>
              <th>Total Resignation</th>
              <th>C.Total Emp</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: 14 }}>
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: 14 }}>
                  No Records Found
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={`${r.fld_departmentname}-${idx}`}>
                  <td>{idx + 1}</td>
                  <td className="left">{r.fld_departmentname ?? ""}</td>
                  <td>{Number(r.Noofemployees || 0).toFixed(0)}</td>
                  <td>{Number(r.inactiveuser || 0).toFixed(0)}</td>
                  <td>{Number(r.activeusers || 0).toFixed(0)}</td>
                </tr>
              ))
            )}
          </tbody>

          {/* Totals row (matches ASP totals) */}
          <tfoot>
            <tr>
              <td colSpan={2}>Total Employees</td>
              <td>{Number(totals.totalEmployees || 0).toFixed(0)}</td>
              <td>{Number(totals.totalResignation || 0).toFixed(0)}</td>
              <td>{Number(totals.totalCurrent || 0).toFixed(0)}</td>
            </tr>
          </tfoot>
        </table>

        <div className="note">
          Note: <span className="muted">This is a system generated report.</span>
        </div>
      </div>
    </div>
  );
};

export default EmployeeOverallReportPrint;
